import { defaultApiContextValue } from '@src/app/context/Api/includes/functions';
import { apis } from '@src/shared/api';
import { ErrorHandler } from '@src/shared/ui/components/ErrorHandler';
import { QueryClientProvider } from '@tanstack/react-query';
import { ReactQueryDevtools } from '@tanstack/react-query-devtools';
import { isUndefined } from 'lodash';
import React, { createContext, useContext, useEffect, useMemo, useState } from 'react';

import { queryClient } from './includes/configuration';

/**
 * defaultContext
 */
const defaultContext = {
  apiKey: 'Default',
  clearApiCache: () => {},
  mutations: {},
  queries: {},
  queryClient: {},
  setApiKey: () => {},
  useApiMutation: () => {},
  useApiQueries: () => {},
  useApiQuery: () => {},
};

/**
 * defaultApiContextValue
 *
 * Creates a default context value for the API context.
 *
 * @type {React.Context<{apiKey: string, clearApiCache: defaultContext.clearApiCache, mutations: {}, queries: {},
 *     queryClient: {}, setApiKey: defaultContext.setApiKey, useApiMutation: defaultContext.useApiMutation,
 *     useApiQueries: defaultContext.useApiQueries, useApiQuery: defaultContext.useApiQuery}>}
 */
const ApiContext = createContext(defaultContext);

/**
 * useApiContext
 *
 * Custom hook to access the API context.
 *
 * @returns {{queryClient: {}, clearApiCache: clearApiCache, mutations: {}, apiKey: string, useApiQuery: useApiQuery,
 *     setApiKey: setApiKey, useApiMutation: useApiMutation, useApiQueries: useApiQueries, queries: {}}}
 */
const useApiContext = () => {
  const context = useContext(ApiContext);
  if (!context) {
    throw new Error('useApiContext must be used within an ApiProvider');
  }
  return context;
};

/**
 * useApi
 *
 * Custom hook to access the API context and set the API key.
 *
 * @param apiKey
 * @returns {{queryClient: QueryClient, clearApiCache: ((function([]=): (Promise<Awaited<void>[]>|Promise<void>))|*),
 *     mutations: *, apiKey, useApiQuery: (function(*, *): *[]), setApiKey, useApiMutation: (function(*, *): *[]),
 *     useApiQueries: (function(*): {}), queries: *}|{queryClient: {}, clearApiCache: clearApiCache, mutations: {},
 *     apiKey: string, useApiQuery: useApiQuery, setApiKey: setApiKey, useApiMutation: useApiMutation, useApiQueries:
 *     useApiQueries, queries: {}}}
 */
export const useApi = (apiKey = 'Default') => {
  const theContext = useApiContext();

  useEffect(() => {
    if (theContext.apiKey !== apiKey) {
      theContext.setApiKey(apiKey);
    }
  }, [apiKey]);

  if (isUndefined(apis?.[apiKey])) {
    throw new Error(`The API with key "${apiKey}" is not found.`);
  }

  if (apiKey !== theContext.apiKey) {
    return defaultApiContextValue(apiKey, theContext.setApiKey);
  }

  return theContext;
};

/**
 * ApiProvider
 *
 * Provides the API context to the application.
 *
 * @param children
 * @returns {Element}
 * @constructor
 */
export function ApiProvider({ children }) {
  const [apiKey, setApiKey] = useState('Default');

  const value = useMemo(() => {
    return defaultApiContextValue(apiKey, setApiKey);
  }, [apiKey]);

  return (
    <ErrorHandler location="src/app/context/Api/Api.jsx">
      <QueryClientProvider client={queryClient}>
        <ApiContext.Provider value={value}>{children}</ApiContext.Provider>
        <ReactQueryDevtools initialIsOpen={false} />
      </QueryClientProvider>
    </ErrorHandler>
  );
}
